import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useAlerts } from '@bw/foundry';
import useSearchParams from 'utils/useQueryParams';
import logEvent, { LogEvent, LogEventStatus } from 'utils/logEvent';
import { datadogLogs } from '@datadog/browser-logs';
import SignIn from './SignIn';
import validAlerts from './ValidAlerts';
import PageTitle from 'components/PageTitle';
import { CLOCK_SKEW_ERROR_MESSAGES } from 'utils/useHandleAuthErrors';
import { OAuthError } from '@okta/okta-auth-js';
import allowReturnToUrl from 'pages/Login/allowReturnToUrl';

const Login: FC = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const { show } = useAlerts();
  const navigate = useNavigate();
  const [{ returnTo, alert, authType }] = useSearchParams();

  useEffect(() => {
    if (returnTo) {
      try {
        const returnToUrl = new URL(returnTo);

        if (allowReturnToUrl(returnToUrl)) {
          oktaAuth.setOriginalUri(returnTo);
          // Remove when DashboardRedirect is removed from Routes.tsx
          sessionStorage.setItem('returnTo', returnTo);
        } else {
          datadogLogs.logger.warn(`Unlisted returnTo url`, {
            data: {
              invalidReturnTo: returnTo,
            },
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Invalid returnTo URL', returnTo);
      }
    }
    authType === 'popup' &&
      sessionStorage.setItem('loginData', JSON.stringify({ authType }));
    Object.keys(validAlerts).includes(alert) &&
      show(validAlerts[alert].type, validAlerts[alert].message);
  }, [oktaAuth, show, authState, returnTo, alert, authType, navigate]);

  useEffect(() => {
    (async () => {
      // Catch external IdPs redirecting back after auth
      if (oktaAuth.isLoginRedirect() && !authState?.isAuthenticated) {
        try {
          sessionStorage.setItem('idpr', 'true');
          await oktaAuth.handleLoginRedirect();
        } catch (err) {
          sessionStorage.removeItem('idpr');
          // Perhaps handle a token error here?

          if (err instanceof OAuthError && err.errorCode === 'INTERNAL') {
            if (CLOCK_SKEW_ERROR_MESSAGES.includes(err.errorSummary)) {
              navigate('/clock-error');
              logEvent(LogEvent.IdP, LogEventStatus.HandledError, {
                error: err,
              });
            }
          } else {
            logEvent(LogEvent.IdP, LogEventStatus.UnhandledError, {
              error: err,
            });
            show(
              'error',
              (err as Error)?.message ||
                'Something went wrong. Please try again.',
            );
            navigate('/login');
          }
          oktaAuth.authStateManager.updateAuthState();
        }
      }
    })();
  }, [oktaAuth, authState, show, navigate]);

  if (!authState || (oktaAuth.isLoginRedirect() && !authState.isAuthenticated))
    return null;

  if (authState.isAuthenticated) {
    navigate('/');
  }
  return (
    <>
      <PageTitle
        title="Log in - Bandwidth"
        description="Log in to Bandwidth to manage your voice, messaging, phone number, and emergency services."
      />
      <SignIn />
    </>
  );
};

export default Login;
